/**
 * In-depth guides to each kind of easement a property owner actually meets.
 *
 * These pages answer the plain "what is a ___ easement" search demand and then
 * carry the reader into the practical work: how the easement shows up on a
 * title commitment, what it does to building plans and value, an annotated
 * example of real recorded wording, and how the easement ends.
 */

import type { ContentSource } from "@/lib/content-review";

export type ClauseAnnotation = { phrase: string; meaning: string };

export type EasementTypeGuide = {
  slug: string;
  name: string;
  /** Short label for cards, breadcrumbs and nav. */
  shortName: string;
  /** Other names people search for. */
  aliases: string[];
  /** One-sentence answer, used for the meta description and the page lead. */
  summary: string;
  /** Three paragraphs of plain-English explanation. */
  whatItIs: string[];
  /** How the easement typically shows up in the records. */
  howItAppears: string[];
  /** What it means if you want to build. */
  buildImpact: string[];
  /** What it means if you are buying. */
  buyImpact: string[];
  sampleClause: {
    title: string;
    text: string;
    annotations: ClauseAnnotation[];
  };
  /** The realistic ways this easement ends or is narrowed. */
  howItEnds: string[];
  keyFacts: { label: string; value: string }[];
  faq: { question: string; answer: string }[];
  sources: ContentSource[];
  relatedGlossary: string[];
  relatedQuestions: string[];
  relatedTypes: string[];
};

export const EASEMENT_TYPES: EasementTypeGuide[] = [
  {
    slug: "utility-easement",
    name: "Utility easement",
    shortName: "Utility",
    aliases: ["power line easement", "electric easement", "PUE", "public utility easement"],
    summary:
      "A utility easement gives an electric, gas, water, sewer or telecom provider the recorded right to install and maintain equipment inside a defined strip of your land.",
    whatItIs: [
      "A utility easement is the most common encumbrance on an American residential lot. It is a recorded grant that lets a named provider, or, on a subdivision plat, any provider the developer dedicated the strip to, enter a described part of your property to install, inspect, repair, replace and remove lines and equipment. You keep ownership of the ground and usually keep the right to use the surface, as long as your use does not interfere with theirs.",
      "The width is what matters. A rear-lot distribution easement is often 5 to 10 feet; a transmission corridor can be 75 to 150 feet, and the wider it is, the more of the lot is effectively unbuildable. Some older grants are blanket easements with no described strip at all: the utility may place facilities anywhere on the parcel until it fixes a location by actually building, at which point the corridor is treated as set.",
      "Utility easements are almost always perpetual and almost always transferable within the industry. When a rural cooperative sells its lines to a regional utility, the easement goes with them. That is why the name in a forty-year-old grant rarely matches the company that shows up with a bucket truck.",
    ],
    howItAppears: [
      "As a Schedule B exception in the title commitment, phrased as \"Easement granted to [utility] recorded in Book ___, Page ___\".",
      "As a dashed strip labeled \"P.U.E.\" or \"10' Utility Easement\" along a lot line on the recorded subdivision plat.",
      "As a separately recorded easement deed with a metes-and-bounds or centerline description, common outside platted subdivisions.",
      "As physical evidence. Pedestals, pad-mounted transformers, guy wires, manhole lids, pole lines and cleared vegetation corridors.",
    ],
    buildImpact: [
      "Permanent structures inside the corridor are usually prohibited outright: houses, garages, additions, decks on footings, and in-ground pools.",
      "Fences are often allowed only with removable panels or a gate for access, and the utility may remove a fence without replacing it if it blocks a repair.",
      "Deep-rooted or tall species are commonly barred; utilities cut trees inside the corridor on their own schedule and are rarely liable for replacing them.",
      "Grade changes matter more than owners expect. Adding two feet of fill over a buried line, or cutting the cover down, both violate typical maintenance clauses.",
    ],
    buyImpact: [
      "Measure the strip against the buildable envelope before you write an offer, not after inspection: a 15-foot rear easement on a shallow lot can eliminate the only place an addition would fit.",
      "Ask whether the corridor is described or blanket. A blanket easement is a genuine title problem worth raising with the seller.",
      "Look for a recorded encroachment: a shed, pool or fence already sitting inside the corridor becomes your liability at closing.",
      "Overhead transmission lines usually affect resale price more than buried distribution lines. Local comparable sales are the honest way to size that, not a rule of thumb.",
    ],
    sampleClause: {
      title: "Typical utility easement grant",
      text:
        "Grantor hereby grants and conveys unto Grantee, its successors and assigns, a perpetual easement and right-of-way ten (10) feet in width for the purpose of constructing, operating, maintaining, repairing, replacing and removing electric distribution facilities, together with the right of ingress and egress over adjoining lands of Grantor, and the right to trim, cut and remove trees and other obstructions that may endanger or interfere with said facilities.",
      annotations: [
        {
          phrase: "successors and assigns",
          meaning:
            "The right is not personal to the company named. It survives mergers and sales, which is why the grantee name is often obsolete.",
        },
        {
          phrase: "perpetual",
          meaning: "No expiry date. It ends only by release, condemnation, or formal abandonment.",
        },
        {
          phrase: "ten (10) feet in width",
          meaning:
            "The described width is the corridor you cannot build in. If the grant gives no width, treat it as a blanket easement.",
        },
        {
          phrase: "ingress and egress over adjoining lands",
          meaning:
            "Crews may cross the rest of your lot to reach the corridor, which is why a fully fenced yard often needs a gate.",
        },
        {
          phrase: "trim, cut and remove trees",
          meaning:
            "Vegetation management on the utility's schedule, generally without compensation for the trees removed.",
        },
      ],
    },
    howItEnds: [
      "A recorded release or quitclaim from the utility, usually after the facilities are relocated at the owner's cost.",
      "Formal abandonment: the lines are removed and the utility records an abandonment instrument. Simply going unused is rarely enough.",
      "Relocation agreements during redevelopment, where a new corridor is granted and the old one released in the same transaction.",
      "Condemnation or vacation by the public body that holds a dedicated plat easement, typically as part of a street or subdivision vacation.",
    ],
    keyFacts: [
      { label: "Typical residential width", value: "5 to 15 feet along a lot line" },
      { label: "Typical duration", value: "Perpetual" },
      { label: "Who holds it", value: "A utility provider or the public generally, by plat dedication" },
      { label: "Usual restriction", value: "No permanent structures, limited planting, access must stay clear" },
    ],
    faq: [
      {
        question: "Can the utility come onto my property without telling me?",
        answer:
          "For routine maintenance inside the corridor, usually yes, the grant itself is the permission. Many utilities give courtesy notice for planned work, and emergency repairs are never noticed in advance. Notice obligations, where they exist, come from the wording of the grant or a state statute, not from a general rule.",
      },
      {
        question: "Do I still pay property taxes on the easement strip?",
        answer:
          "Yes. You still own the land; the utility owns a right to use it. Some assessors reduce the assessed value of a heavily encumbered strip, especially under a transmission corridor, but you have to raise it with the assessor rather than expecting it automatically.",
      },
      {
        question: "Can I get paid for a utility easement already on my land?",
        answer:
          "Not for one that already exists, that compensation was paid to a previous owner, or the strip was dedicated when the subdivision was platted. Payment comes up when a utility asks for a new or wider easement, and that is a negotiation about the value of what the new corridor takes away.",
      },
      {
        question: "What if the pole is not where the easement says it should be?",
        answer:
          "That is a real and common discrepancy. Equipment installed outside the described corridor may be an encroachment the utility needs to correct, or may point to a second unrecorded grant. A surveyor's location of the facilities against the recorded description is what settles it.",
      },
    ],
    sources: [
      {
        label: "FHWA. Utility accommodation and relocation",
        url: "https://www.fhwa.dot.gov/programadmin/utility.cfm",
        note: "Federal guidance on utility occupancy of rights-of-way.",
      },
      {
        label: "Common Ground Alliance. 811 damage prevention",
        url: "https://commongroundalliance.com/",
        note: "Locate-before-you-dig requirements that apply inside utility corridors.",
      },
    ],
    relatedGlossary: ["utility-easement", "blanket-easement", "servient-estate", "buildable-envelope"],
    relatedQuestions: [
      "what-is-a-utility-easement-on-my-property",
      "can-i-plant-trees-in-a-utility-easement",
      "how-do-utility-easements-affect-property-value",
    ],
    relatedTypes: ["right-of-way-easement", "drainage-easement", "easement-in-gross"],
  },

  {
    slug: "access-easement",
    name: "Access easement (driveway easement)",
    shortName: "Access / driveway",
    aliases: ["driveway easement", "ingress and egress easement", "shared driveway"],
    summary:
      "An access easement gives a specific parcel the recorded right to cross another parcel to reach a public road, most often over a shared driveway or private lane.",
    whatItIs: [
      "An access easement solves a geography problem: one lot cannot reach a public road without crossing another. The grant names a route, a described strip, a centerline with a width, or sometimes simply \"the existing driveway\". And states the permitted purpose, normally ingress and egress for the benefited lot.",
      "Access easements are usually appurtenant, meaning the right belongs to the benefited parcel rather than to a person. Sell the benefited lot and the buyer gets the access automatically; sell the burdened lot and the buyer takes it subject to the access, whether or not anyone mentioned it at the kitchen table.",
      "The two recurring fights are scope and maintenance. Scope: a right of access for a single residence does not automatically stretch to a second dwelling, a short-term rental operation, or heavy commercial traffic. Maintenance: many older grants say nothing at all about who pays to grade, gravel, plow or repave, and silence is what turns neighbors into litigants.",
    ],
    howItAppears: [
      "A Schedule B exception referencing an \"easement for ingress and egress\" with a book and page number.",
      "A plat note creating a private access easement or a \"private drive\" tract serving several lots.",
      "A road maintenance agreement recorded alongside the easement, sometimes with a cost-sharing formula.",
      "On the ground: a driveway that visibly serves more than one house, or a lane crossing a lot line at an angle no single-lot driveway would take.",
    ],
    buildImpact: [
      "You cannot gate, block, narrow or reroute the traveled way unilaterally, even when the pavement sits on your deed.",
      "Structures, landscaping and parking that force the easement holder to maneuver around them are usually treated as interference.",
      "Adding your own driveway connection inside the easement is normally fine as long as the holder's use is not materially impaired.",
      "Planned improvements, widening for a fire-code turnaround, adding a culvert, should be documented in an amendment signed by both owners and recorded.",
    ],
    buyImpact: [
      "If the lot you are buying depends on someone else's land for access, verify the easement is recorded, not merely historical use. Lenders and title insurers ask this too.",
      "Read the width against reality. A 12-foot recorded easement serving a driveway that is physically 20 feet wide means part of what you drive on is not covered.",
      "Confirm whether the easement supports the use you intend, including construction traffic during a build.",
      "Look for a maintenance clause. If there is none, negotiate one before closing while the other owner still has a reason to cooperate.",
    ],
    sampleClause: {
      title: "Typical shared-driveway grant",
      text:
        "Grantor grants to Grantee, as an easement appurtenant to and running with Lot 4, a non-exclusive easement twenty (20) feet in width for ingress and egress over and across the existing driveway on Lot 3, the location of which is more particularly described on Exhibit A, said easement to be used in common with Grantor and to be maintained by the parties in equal shares.",
      annotations: [
        {
          phrase: "appurtenant to and running with Lot 4",
          meaning: "The benefit belongs to the lot, so it transfers automatically on every future sale.",
        },
        {
          phrase: "non-exclusive",
          meaning: "The owner of the burdened lot keeps the right to use the same driveway.",
        },
        {
          phrase: "ingress and egress",
          meaning:
            "Travel in and out only. It is not parking, not storage, and not a right to install utilities unless the grant says so.",
        },
        {
          phrase: "more particularly described on Exhibit A",
          meaning:
            "The exhibit controls the location. If the exhibit is missing from the recorded copy, the described route is ambiguous and should be corrected.",
        },
        {
          phrase: "maintained by the parties in equal shares",
          meaning:
            "A rare and valuable sentence: it fixes cost-sharing. Without it, state law or a court has to fill the gap.",
        },
      ],
    },
    howItEnds: [
      "Merger: one owner buys both parcels, and the easement extinguishes because you cannot hold an easement over your own land.",
      "A recorded release signed by the owner of the benefited parcel, usually when a new public street makes the route unnecessary.",
      "Abandonment where the holder's conduct shows a clear intent to give up the route, not merely years of non-use.",
      "Court-ordered termination or relocation, available in some states when the purpose has become impossible or the route can be moved without harming the holder.",
    ],
    keyFacts: [
      { label: "Usual form", value: "Appurtenant. Attached to the benefited lot" },
      { label: "Typical width", value: "12 to 30 feet, depending on fire-access rules" },
      { label: "Common gap", value: "No maintenance or cost-sharing clause" },
      { label: "Key limit", value: "Scope is fixed by the stated purpose, not by convenience" },
    ],
    faq: [
      {
        question: "Can I put a gate across a shared driveway?",
        answer:
          "Only if it does not materially interfere with the other owner's use. In practice that means the holder gets a key or a code and the gate does not create delays or a hazard. Installing a locked gate first and negotiating later is the fastest route to an injunction.",
      },
      {
        question: "Who pays to repave a shared driveway?",
        answer:
          "Whatever the recorded agreement says. With no agreement, many states apply a rule that each user contributes in proportion to use, but enforcing that means a lawsuit. Recording a short maintenance agreement while relations are good is dramatically cheaper.",
      },
      {
        question: "Can the neighbor add houses and keep using my driveway?",
        answer:
          "Increasing the burden beyond the original purpose is the classic overuse claim. An easement granted for one residence does not automatically serve a subdivision. Whether added traffic counts as overuse turns on the grant's wording and on state case law.",
      },
      {
        question: "Does a shared driveway hurt resale value?",
        answer:
          "It can, mainly through buyer hesitancy and lender questions rather than the physical driveway. A clean recorded easement with a written maintenance formula sells far better than an informal arrangement neighbors describe verbally.",
      },
    ],
    sources: [
      {
        label: "Cornell LII, easements overview",
        url: "https://www.law.cornell.edu/wex/easement",
        note: "General doctrine on appurtenant easements, scope and interference.",
      },
      {
        label: "FEMA / ICC fire apparatus access requirements",
        url: "https://codes.iccsafe.org/content/IFC2021P1/chapter-5-fire-service-features",
        note: "Access width and turnaround standards that shape driveway easement design.",
      },
    ],
    relatedGlossary: ["right-of-way", "appurtenant-easement", "dominant-estate", "servient-estate"],
    relatedQuestions: [
      "can-my-neighbor-use-my-driveway",
      "who-maintains-a-shared-driveway",
      "can-a-neighbor-block-my-easement",
    ],
    relatedTypes: ["easement-by-necessity", "right-of-way-easement", "appurtenant-easement"],
  },

  {
    slug: "easement-by-necessity",
    name: "Easement by necessity",
    shortName: "By necessity",
    aliases: ["landlocked property access", "way of necessity"],
    summary:
      "An easement by necessity is access a court implies, not one anybody recorded. When a parcel was cut off from a public road by the same owner who once held both pieces.",
    whatItIs: [
      "An easement by necessity is a remedy for landlocked land. Courts imply it because the law prefers usable property over stranded property, and because the parties to the original split almost certainly intended access even though nobody wrote it down.",
      "Nearly every state requires the same core elements: the burdened and benefited parcels were once under common ownership, that ownership was severed by a conveyance, and the necessity for access arose at the moment of severance. Necessity created later, a bridge washes out, a public road is vacated, generally does not qualify, which surprises owners who assume any landlocked parcel gets a route.",
      "How strict the necessity must be varies by state. Some require strict necessity, meaning no legal access at all; others accept reasonable necessity, where an existing route is impractical rather than nonexistent. Access that is legally available but physically difficult. A steep gully, a seasonal ford, is exactly where those two standards produce different outcomes.",
    ],
    howItAppears: [
      "Rarely in the title commitment, because nothing was recorded. It surfaces as a title objection or an access exception instead.",
      "In a deed history: a parent tract split into pieces, one of which no longer touches a road.",
      "In a court judgment or a settlement deed, once the easement has been established and recorded.",
      "As a lender or insurer requirement, many will not close on a parcel without legal access confirmed in writing.",
    ],
    buildImpact: [
      "Until a route is established and recorded, building permits are frequently refused for lack of legal access.",
      "Courts set the location of an implied route, and they usually pick the least burdensome path for the servient owner, not the most convenient one for the holder.",
      "The scope tends to be limited to the necessity: access for a residence, not for logging trucks or a quarry, unless the original use justified it.",
      "Utility installation is not automatically included. Many states treat utility access as a separate question from vehicular access.",
    ],
    buyImpact: [
      "Never buy a landlocked parcel on the assumption a necessity easement exists. Verify the common-ownership history first, in the records, before the contract goes hard.",
      "Ask what the seller has actually been using and under what claim. A neighbor's permission is a license, revocable at will, not an easement.",
      "Budget for the establishment cost. A title search back to the severance deed, a survey, and often a quiet-title action.",
      "Get the resulting route recorded with a legal description. An unrecorded court right is a problem you pass to the next buyer.",
    ],
    sampleClause: {
      title: "Language from a judgment establishing a way of necessity",
      text:
        "The Court finds that Parcel B was severed from the common parent tract by deed recorded at Book 412, Page 88, that Parcel B has no other legal access to a public way, and that a way of necessity therefore exists. The Court establishes a thirty (30) foot access easement along the northerly boundary of Parcel A as depicted on the survey admitted as Exhibit 3, for ingress, egress and regress serving one single-family residence.",
      annotations: [
        {
          phrase: "severed from the common parent tract",
          meaning:
            "The unity-then-severance element. Without it there is no easement by necessity, however landlocked the parcel is.",
        },
        {
          phrase: "no other legal access",
          meaning:
            "The strict-necessity standard. Under a reasonable-necessity standard, an impractical route can still support the claim.",
        },
        {
          phrase: "along the northerly boundary",
          meaning:
            "The court chooses a location, typically the one that burdens the servient parcel least.",
        },
        {
          phrase: "serving one single-family residence",
          meaning:
            "Scope is tied to the necessity. Intensifying use later can require going back to court.",
        },
      ],
    },
    howItEnds: [
      "The necessity ends: a new public road or a purchased alternative route reaches the parcel, and in most states the implied easement extinguishes with it.",
      "Merger, when the landlocked parcel and the burdened parcel come back under one owner.",
      "A recorded release once an express easement replaces the implied one.",
      "Replacement by agreement, the parties record a described express easement, which is the outcome worth pushing for in every negotiation.",
    ],
    keyFacts: [
      { label: "Created by", value: "Court order, not a signed grant" },
      { label: "Core test", value: "Common ownership, severance, necessity at severance" },
      { label: "Standard", value: "Strict necessity in some states, reasonable necessity in others" },
      { label: "Ends when", value: "The necessity ends, or the parcels merge" },
    ],
    faq: [
      {
        question: "Is a landlocked parcel automatically entitled to access?",
        answer:
          "No. The parcel must trace back to a common owner who severed it and created the landlock. A tract that was landlocked from the start, or landlocked by an event after severance, generally does not qualify, and the owner has to negotiate an express easement instead.",
      },
      {
        question: "Who chooses where the route goes?",
        answer:
          "The servient owner often gets the first say, and if the parties cannot agree the court fixes it. Courts favor an existing lane or a boundary line over a route that cuts a working parcel in half.",
      },
      {
        question: "Do I have to pay for a way of necessity?",
        answer:
          "Usually there is no purchase price, because the easement is implied from the original transaction rather than bought. Costs still land on the holder: the litigation, the survey, and typically construction and maintenance of the route.",
      },
      {
        question: "How is this different from an easement by prescription?",
        answer:
          "Necessity is implied from a broken chain of access at the moment of severance. Prescription is earned by years of open, continuous use without permission. Necessity does not require any past use at all; prescription requires nothing but it.",
      },
    ],
    sources: [
      {
        label: "Cornell LII, easement by necessity",
        url: "https://www.law.cornell.edu/wex/implied_easement_by_necessity",
      },
      {
        label: "National Agricultural Law Center. Access to landlocked land",
        url: "https://nationalaglawcenter.org/",
        note: "State-by-state research on rural access disputes.",
      },
    ],
    relatedGlossary: ["easement-by-necessity", "dominant-estate", "servient-estate", "survey"],
    relatedQuestions: [
      "what-is-an-easement-by-necessity",
      "can-a-neighbor-block-my-easement",
      "how-to-find-easements-on-my-property",
    ],
    relatedTypes: ["access-easement", "prescriptive-easement", "appurtenant-easement"],
  },

  {
    slug: "prescriptive-easement",
    name: "Prescriptive easement",
    shortName: "Prescriptive",
    aliases: ["easement by prescription", "adverse use easement"],
    summary:
      "A prescriptive easement is a right of use earned by years of open, continuous use without the owner's permission, the use-based cousin of adverse possession.",
    whatItIs: [
      "A prescriptive easement is created by conduct rather than paperwork. Someone uses part of another person's land, a lane, a path, a parking area, a drainage outfall. Openly and without permission, and after the statutory period the law converts that habit into a legal right.",
      "The elements repeat across states with local variations: the use must be open and notorious, continuous for the statutory period, hostile or adverse (that is, without permission), and in many states exclusive of the general public or under a claim of right. The clock length is the biggest state-to-state difference and runs roughly from five to twenty years.",
      "Crucially, prescription produces a right to use, not ownership. That distinguishes it from adverse possession, which transfers title. A neighbor who wins a prescriptive easement to drive across your yard does not own the strip; you still hold it, pay tax on it, and may use it yourself in any way that does not block them.",
    ],
    howItAppears: [
      "Almost never in a title commitment. Title insurers exclude rights of parties in possession that a survey or inspection would reveal.",
      "On a survey as a worn track, fence line, or use that does not match a recorded document.",
      "In a demand letter or quiet-title complaint, once the user asserts the right formally.",
      "As an owner's affidavit or a recorded notice interrupting the use, filed to stop the clock.",
    ],
    buildImpact: [
      "Blocking a mature prescriptive use invites an injunction ordering you to remove whatever you built.",
      "Before fencing a boundary, find out how long the neighbor has been crossing and compare it with your state's prescriptive period.",
      "A permissive-use letter, signed and dated, defeats hostility going forward and is the cheapest defense available.",
      "Some states allow the owner to record a notice of interruption; where available, it resets the clock without a confrontation.",
    ],
    buyImpact: [
      "Walk the property looking for worn paths, gates in odd places, or utility runs with no recorded grant.",
      "Ask the seller directly, in writing, who else uses the land and under what arrangement. Their written answer matters later.",
      "Understand that a survey exception in the title policy leaves you uninsured against exactly this risk.",
      "If the use is long-standing, negotiate an express easement or a written license before closing rather than inheriting an argument.",
    ],
    sampleClause: {
      title: "Language from a decree confirming a prescriptive easement",
      text:
        "The Court finds that Plaintiffs and their predecessors have used the gravel lane across Defendant's parcel openly, notoriously, continuously and adversely, under a claim of right and without permission of the owner, for a period in excess of the statutory period, and the Court therefore confirms a prescriptive easement for ingress and egress limited to the width and location of the lane as historically used.",
      annotations: [
        {
          phrase: "and their predecessors",
          meaning:
            "Tacking. Successive owners' periods of use can be added together to reach the statutory period.",
        },
        {
          phrase: "openly, notoriously",
          meaning:
            "The use had to be visible enough that a reasonable owner would have noticed and could have objected.",
        },
        {
          phrase: "without permission of the owner",
          meaning:
            "Permission is the whole defense. A signed license, even a friendly one, prevents the right from ripening.",
        },
        {
          phrase: "limited to the width and location as historically used",
          meaning:
            "The prescriptive right is measured by the actual past use, which is why these easements are usually narrower than negotiated ones.",
        },
      ],
    },
    howItEnds: [
      "The holder abandons the use with evident intent to give it up, non-use alone usually is not enough.",
      "A recorded release, often negotiated as part of a boundary settlement.",
      "Merger of the two parcels under one owner.",
      "A successful challenge before the statutory period is complete, including recorded interruption where state law provides for it.",
    ],
    keyFacts: [
      { label: "Created by", value: "Years of use, then confirmed by a court" },
      { label: "Typical period", value: "5 to 20 years, set by state statute" },
      { label: "Gives", value: "A right to use, not ownership" },
      { label: "Best defense", value: "Written permission, which destroys the hostility element" },
    ],
    faq: [
      {
        question: "How many years does a prescriptive easement take?",
        answer:
          "It depends entirely on the state; the range across the country runs from about five to twenty years. Our state easement law pages list the period for each state we cover, with a link to the statute so you can read the exact language yourself.",
      },
      {
        question: "Does giving permission stop the clock?",
        answer:
          "Yes, and it is the single most effective step available. Permission removes the adverse element, converting the use into a revocable license. Put it in writing, date it, and keep a copy, an unwritten conversation is hard to prove years later.",
      },
      {
        question: "Can the public gain a prescriptive easement over a private path?",
        answer:
          "Some states recognize public prescriptive rights or implied dedication where the public has used a beach path or trail for decades. Others have enacted statutes specifically to prevent it. This is one of the most state-specific corners of easement law.",
      },
      {
        question: "Is a prescriptive easement the same as adverse possession?",
        answer:
          "No. Adverse possession transfers ownership of land and typically requires exclusive possession, often with payment of taxes. Prescription grants only a use right and tolerates the owner's continued use of the same ground.",
      },
    ],
    sources: [
      {
        label: "Cornell LII. Prescriptive easement",
        url: "https://www.law.cornell.edu/wex/prescriptive_easement",
      },
      {
        label: "State legislature statute portals",
        url: "https://www.congress.gov/state-legislature-websites",
        note: "Prescriptive periods are set by each state's own code.",
      },
    ],
    relatedGlossary: ["prescriptive-easement", "adverse-possession", "encroachment", "survey"],
    relatedQuestions: [
      "what-is-a-prescriptive-easement",
      "can-my-neighbor-use-my-driveway",
      "can-i-remove-an-easement-from-my-property",
    ],
    relatedTypes: ["easement-by-necessity", "access-easement", "utility-easement"],
  },

  {
    slug: "appurtenant-easement",
    name: "Appurtenant easement",
    shortName: "Appurtenant",
    aliases: ["easement appurtenant", "easement running with the land"],
    summary:
      "An appurtenant easement benefits a parcel rather than a person, so it transfers automatically every time either property is sold.",
    whatItIs: [
      "An appurtenant easement always involves two parcels: the dominant estate, which holds the benefit, and the servient estate, which carries the burden. Access across a neighbor's lane, a shared driveway, a view or light easement, a right to draw water from a pond, all of these are typically appurtenant.",
      "The defining feature is that the right attaches to the land. Nobody has to reassign it at closing, nobody has to sign anything, and it cannot be sold separately from the benefited parcel. A buyer of the dominant estate receives the benefit whether or not the agent mentioned it; a buyer of the servient estate takes the burden whether or not anyone pointed at it during the walkthrough.",
      "When a grant is ambiguous about whether it is appurtenant or in gross, most states presume it is appurtenant. Courts prefer the reading that keeps land useful, and a right tied to a parcel is easier to trace decades later than one tied to a person who may have died or moved.",
    ],
    howItAppears: [
      "In the deed for the dominant estate as a granting phrase: \"together with an easement over...\".",
      "In the deed for the servient estate as a reservation or a \"subject to\" clause.",
      "In the title commitment on both sides, as an insured appurtenance for the benefited lot, and as a Schedule B exception for the burdened lot.",
      "On the plat, where private access or utility easements are shown running between numbered lots.",
    ],
    buildImpact: [
      "On the servient estate, anything that materially interferes with the benefited use is exposed to a removal order.",
      "On the dominant estate, you generally cannot expand the easement to serve land it never served. Including a newly acquired adjoining parcel.",
      "Improvements inside the easement are usually the holder's right and responsibility, unless the grant assigns them otherwise.",
      "Both owners can normally use the easement area so long as neither materially interferes with the other.",
    ],
    buyImpact: [
      "Buying the dominant estate: confirm the easement is recorded and its description actually reaches your parcel. An appurtenant benefit that never got recorded is not a benefit you can rely on.",
      "Buying the servient estate: read the scope. \"Ingress and egress\" is narrow; \"for all purposes\" is very wide.",
      "Check whether the benefit is insured in your title policy, appurtenant rights can be listed as insured estates, which matters if they are later challenged.",
      "Verify the easement has not been extinguished by a prior merger, which happens quietly when one owner briefly held both parcels.",
    ],
    sampleClause: {
      title: "Appurtenant grant in a deed",
      text:
        "TOGETHER WITH a perpetual, non-exclusive easement appurtenant to the land herein conveyed, over, upon and across the westerly twenty (20) feet of Lot 7, for ingress, egress and the installation and maintenance of underground utilities, which easement shall run with the land and be binding upon and inure to the benefit of the parties, their heirs, successors and assigns.",
      annotations: [
        {
          phrase: "TOGETHER WITH",
          meaning:
            "Signals that the deed is conveying a benefit over other land, not just the parcel itself. On the burdened side, the equivalent phrase is \"SUBJECT TO\".",
        },
        {
          phrase: "appurtenant to the land herein conveyed",
          meaning:
            "Names the dominant estate. This is the phrase that makes the right transfer automatically.",
        },
        {
          phrase: "and the installation and maintenance of underground utilities",
          meaning:
            "A wider scope than access alone. Access-only easements do not include the right to trench for utilities.",
        },
        {
          phrase: "run with the land",
          meaning: "Confirms the intent that the right survives all future conveyances of both parcels.",
        },
      ],
    },
    howItEnds: [
      "Merger, when one person acquires both the dominant and servient estates.",
      "Release recorded by the owner of the dominant estate.",
      "Expiration, where the grant states a term or a condition that has been met.",
      "Abandonment shown by conduct, or extinguishment through a valid tax sale or foreclosure in some jurisdictions.",
    ],
    keyFacts: [
      { label: "Parties", value: "Dominant estate benefits, servient estate is burdened" },
      { label: "Transfers", value: "Automatically with each parcel" },
      { label: "Sellable separately", value: "No" },
      { label: "Default presumption", value: "Ambiguous grants are usually read as appurtenant" },
    ],
    faq: [
      {
        question: "How do I know if an easement is appurtenant or in gross?",
        answer:
          "Look for words tying the right to a parcel. \"appurtenant to Lot 4\", \"running with the land\", \"benefiting the property described in Exhibit A\". Where the grant is silent, most states presume appurtenant. A right granted to a company for its own operations, with no benefited parcel, is in gross.",
      },
      {
        question: "Can I sell my easement to my neighbor?",
        answer:
          "Not on its own. An appurtenant easement cannot be split from the land it benefits. You can release it, or you can sell the benefited parcel with the easement attached, but you cannot transfer the easement to an unrelated owner.",
      },
      {
        question: "Can the servient owner move the easement?",
        answer:
          "Traditionally no without consent. A growing number of states allow relocation at the servient owner's expense when the new route serves the holder just as well. Whether your state follows the modern rule is worth checking before you regrade anything.",
      },
      {
        question: "Does an appurtenant easement have to be recorded?",
        answer:
          "To bind future buyers reliably, yes. An unrecorded easement can still bind someone with actual notice of it, but recording is what protects the right against a later good-faith purchaser.",
      },
    ],
    sources: [
      {
        label: "Cornell LII. Appurtenant easement",
        url: "https://www.law.cornell.edu/wex/appurtenant",
      },
      {
        label: "American Land Title Association, title basics",
        url: "https://www.alta.org/",
        note: "How appurtenant benefits and burdens are treated in title commitments.",
      },
    ],
    relatedGlossary: ["appurtenant-easement", "dominant-estate", "servient-estate", "encumbrance"],
    relatedQuestions: [
      "do-easements-transfer-with-the-property",
      "what-is-an-easement-on-my-property",
      "easement-vs-right-of-way",
    ],
    relatedTypes: ["easement-in-gross", "access-easement", "prescriptive-easement"],
  },

  {
    slug: "easement-in-gross",
    name: "Easement in gross",
    shortName: "In gross",
    aliases: ["commercial easement in gross", "personal easement"],
    summary:
      "An easement in gross belongs to a person or a company rather than to a neighboring parcel, the category most utility, pipeline and billboard easements fall into.",
    whatItIs: [
      "An easement in gross has a burdened parcel but no benefited parcel. The right belongs to the holder directly: an electric cooperative, a pipeline operator, a railroad, a cell carrier, a billboard company, or occasionally an individual granted a personal right such as fishing access.",
      "The law splits these into two groups that behave very differently. Commercial easements in gross. Utility lines, pipelines, rail corridors, are freely transferable and are treated much like business assets; they get bought, sold and consolidated. Personal easements in gross are generally not transferable and end with the holder's death.",
      "Because there is no dominant estate, tracing the current holder can be genuinely hard. A grant to a 1950s telephone cooperative may now sit with a national carrier after four corporate transactions, none of which necessarily produced a recorded assignment in your county.",
    ],
    howItAppears: [
      "A Schedule B exception naming a company rather than an adjoining lot.",
      "A recorded easement deed with a corridor description but no reference to any benefited land.",
      "A memorandum of easement or memorandum of lease, used to record a short summary while keeping commercial terms private.",
      "Physical clues: pipeline markers, rail ballast, cell compounds, guy anchors, and billboard footings.",
    ],
    buildImpact: [
      "Corridor widths are frequently large, a pipeline easement of 50 feet or more can dominate a small parcel's buildable area.",
      "Pipeline and transmission holders typically bar structures, deep excavation and heavy equipment crossings without a written crossing permit.",
      "Because the holder may be a distant corporation, getting approval for a driveway crossing or a fence takes lead time; build that into project schedules.",
      "Depth and cover requirements can make landscaping and grading changes a compliance issue, not just an aesthetic choice.",
    ],
    buyImpact: [
      "Identify the current holder before closing, not after. Ask the title company for the assignment chain.",
      "Read whether the grant is exclusive. An exclusive easement in gross lets the holder exclude even you from the corridor.",
      "Check for a blanket description. Blanket pipeline easements are more common than buyers expect on rural acreage.",
      "For income-producing easements. Billboards, cell sites, confirm who receives the rent after closing, and whether the payment stream was assigned away previously.",
    ],
    sampleClause: {
      title: "Commercial easement in gross",
      text:
        "Grantor grants to Grantee, a corporation, its successors and assigns forever, an exclusive easement fifty (50) feet in width for the construction, operation, maintenance, inspection, alteration, repair, replacement and removal of one or more pipelines for the transportation of natural gas, together with the right to assign this easement in whole or in part.",
      annotations: [
        {
          phrase: "a corporation, its successors and assigns",
          meaning:
            "No benefited parcel is named. The right belongs to the company, which is what makes it in gross.",
        },
        {
          phrase: "exclusive",
          meaning:
            "Stronger than most easements: it can bar the landowner from using the corridor, not merely from interfering.",
        },
        {
          phrase: "one or more pipelines",
          meaning:
            "Permits additional lines later within the same corridor, without a new grant or a new payment.",
        },
        {
          phrase: "right to assign this easement in whole or in part",
          meaning:
            "Explicit transferability. The company you negotiated with may not be the company that shows up.",
        },
      ],
    },
    howItEnds: [
      "Recorded release by the holder, usually after facilities are abandoned in place or removed.",
      "Expiration of a stated term, common in cell-site and billboard easements.",
      "Death of the holder, for a genuinely personal easement in gross.",
      "Statutory abandonment procedures, which several states provide for railroad and pipeline corridors.",
    ],
    keyFacts: [
      { label: "Benefited parcel", value: "None, the holder is a person or company" },
      { label: "Transferable", value: "Yes if commercial, generally no if personal" },
      { label: "Common holders", value: "Utilities, pipelines, railroads, carriers, billboard firms" },
      { label: "Watch for", value: "Exclusive rights and multi-line corridor clauses" },
    ],
    faq: [
      {
        question: "Can a company sell the easement across my land?",
        answer:
          "If it is commercial, generally yes, and often without notifying you. Look for an express right to assign in the grant. The new holder takes the same rights, no more and no less, so the practical change for you is usually who answers the phone.",
      },
      {
        question: "Do I get paid rent for an easement in gross?",
        answer:
          "Only if the grant says so. Utility corridors are usually paid once, at the time of the original grant. Cell sites, billboards and some fiber agreements are structured as recurring payments, and those payment rights can be assigned separately from the land.",
      },
      {
        question: "What happens if the holder disappears?",
        answer:
          "The easement does not vanish with the company. Clearing a stale corridor from your title normally means tracing the corporate successor and getting a release, or using a state abandonment procedure if one exists for that facility type.",
      },
      {
        question: "Is a utility easement always in gross?",
        answer:
          "Usually, but not always. A utility easement dedicated on a plat to serve the subdivision can function more like a public dedication, and a private water-line easement serving a specific neighboring lot is appurtenant to that lot. Read the grant rather than assuming from the label.",
      },
    ],
    sources: [
      {
        label: "Cornell LII. Easement (including easements in gross)",
        url: "https://www.law.cornell.edu/wex/easement",
      },
      {
        label: "PHMSA, pipeline safety and public awareness",
        url: "https://www.phmsa.dot.gov/pipeline/pipeline-safety-awareness",
        note: "Landowner guidance for living near transmission pipelines.",
      },
    ],
    relatedGlossary: ["easement-in-gross", "utility-easement", "encumbrance", "servient-estate"],
    relatedQuestions: [
      "what-is-a-utility-easement-on-my-property",
      "do-easements-transfer-with-the-property",
      "how-do-utility-easements-affect-property-value",
    ],
    relatedTypes: ["utility-easement", "appurtenant-easement", "conservation-easement"],
  },

  {
    slug: "conservation-easement",
    name: "Conservation easement",
    shortName: "Conservation",
    aliases: ["conservation restriction", "agricultural easement", "land trust easement"],
    summary:
      "A conservation easement is a voluntary, recorded restriction that permanently limits development on a property, held and enforced by a land trust or government agency.",
    whatItIs: [
      "A conservation easement is a negative easement: instead of letting someone use your land, it stops you from using it in specified ways. Owners keep title, keep living there, and usually keep farming, grazing or harvesting timber, while surrendering rights such as subdivision, mining, or building beyond agreed envelopes.",
      "The holder is a qualified organization, a land trust, a state agency, or a federal program partner. With a legal duty to monitor and enforce the restrictions, typically through an annual site visit and a baseline documentation report prepared when the easement is signed.",
      "Most conservation easements are perpetual, and that permanence is what makes them eligible for a federal charitable deduction when donated. Term easements exist under some agricultural programs, but a term easement generally does not produce the same tax treatment.",
    ],
    howItAppears: [
      "A recorded deed of conservation easement, often long, with detailed exhibits and a baseline report referenced by date.",
      "A Schedule B exception naming a land trust or a state conservation agency.",
      "A plat or GIS layer showing protected acreage, building envelopes and reserved rights areas.",
      "Program records for federal or state purchase programs, which are generally public.",
    ],
    buildImpact: [
      "New construction is usually confined to designated building envelopes; outside them, even a barn may be prohibited.",
      "Subdivision rights are commonly extinguished entirely, which is often the single largest value effect.",
      "Reserved rights are specific and finite, one additional residence, a defined agricultural footprint, an existing timber plan, and expanding them takes the holder's written consent.",
      "Surface alteration such as grading, ponds, roads and mining is frequently limited or barred outright.",
    ],
    buyImpact: [
      "Read the easement in full before making an offer. Unlike a 10-foot utility strip, this document defines what the entire property may ever become.",
      "Ask the holder for the baseline documentation report and the most recent monitoring letter; unresolved violations transfer with the land.",
      "Confirm whether the deduction was taken by a prior owner. It does not create liability for you, but it explains why the terms are strict and perpetual.",
      "Expect appraisal to be handled as before-and-after: the unrestricted value less the restricted value, which requires an appraiser experienced with conservation easements.",
    ],
    sampleClause: {
      title: "Restriction and reserved-rights language",
      text:
        "Grantor conveys to Holder in perpetuity a conservation easement over the Protected Property for the purpose of preserving its agricultural productivity, scenic open space and wildlife habitat. Grantor shall not subdivide the Protected Property, and no structures shall be constructed except within the Building Envelope depicted on Exhibit B; Grantor reserves the right to construct one additional agricultural structure not exceeding 3,000 square feet within said Envelope with prior written approval of Holder.",
      annotations: [
        {
          phrase: "in perpetuity",
          meaning:
            "Runs with the land forever and binds every future owner. Perpetuity is also a condition of the federal deduction.",
        },
        {
          phrase: "conservation purpose",
          meaning:
            "The stated purposes govern how ambiguous provisions are interpreted later, so the purpose clause is not boilerplate.",
        },
        {
          phrase: "shall not subdivide",
          meaning:
            "Extinguishes development potential, normally the largest single component of the value the owner gives up.",
        },
        {
          phrase: "Building Envelope depicted on Exhibit B",
          meaning:
            "A mapped area where limited construction is allowed. Everything outside it is off limits.",
        },
        {
          phrase: "with prior written approval of Holder",
          meaning:
            "A consent right. Reserved rights are frequently conditioned on the holder agreeing in advance.",
        },
      ],
    },
    howItEnds: [
      "Rarely, and never casually. Most terminations require a court proceeding and a finding that the conservation purpose has become impossible.",
      "Condemnation by a public agency, where proceeds are typically split with the holder by a formula in the easement.",
      "Expiration, only where the easement was written for a defined term under a specific program.",
      "Amendment, which is the realistic path: adjusting boundaries or reserved rights with the holder's agreement and, in some states, regulator review.",
    ],
    keyFacts: [
      { label: "Held by", value: "Land trust or government agency" },
      { label: "Typical duration", value: "Perpetual" },
      { label: "Owner keeps", value: "Title, occupancy and reserved rights" },
      { label: "Valuation method", value: "Before-and-after appraisal of the whole property" },
    ],
    faq: [
      {
        question: "Can I still farm, hunt or live on the land?",
        answer:
          "Almost always yes. Conservation easements are designed to keep working land working. The restrictions target subdivision, commercial or industrial development, mining and habitat destruction, not ordinary agricultural or residential use within the terms.",
      },
      {
        question: "Does a conservation easement lower property value?",
        answer:
          "It typically lowers market value by removing development potential, which is exactly why a donation can generate a charitable deduction. The size of the reduction depends on how much development potential the property actually had, established by a qualified appraisal rather than a percentage rule.",
      },
      {
        question: "Can a future owner remove the easement?",
        answer:
          "Not unilaterally. Perpetual conservation easements bind all successors and can generally be extinguished only by a court on a showing of impossibility, usually with proceeds allocated to the holder for other conservation work.",
      },
      {
        question: "Who checks that the restrictions are followed?",
        answer:
          "The holder does, normally with an annual monitoring visit compared against the baseline documentation report. Violations are addressed first by notice and correction, and by litigation if that fails.",
      },
    ],
    sources: [
      {
        label: "USDA NRCS. Agricultural Conservation Easement Program",
        url: "https://www.nrcs.usda.gov/programs-initiatives/acep-agricultural-conservation-easement-program",
      },
      {
        label: "IRS. Conservation easement deductions",
        url: "https://www.irs.gov/charities-non-profits/conservation-easements",
        note: "Federal requirements for perpetuity, qualified appraisals and qualified holders.",
      },
    ],
    relatedGlossary: ["conservation-easement", "restrictive-covenant", "encumbrance", "survey"],
    relatedQuestions: [
      "what-is-a-conservation-easement",
      "do-easements-transfer-with-the-property",
      "what-is-an-easement-on-my-property",
    ],
    relatedTypes: ["easement-in-gross", "utility-easement", "drainage-easement"],
  },

  {
    slug: "drainage-easement",
    name: "Drainage easement",
    shortName: "Drainage",
    aliases: ["stormwater easement", "detention easement", "swale easement"],
    summary:
      "A drainage easement reserves part of a lot for stormwater to collect, flow or be stored, and it usually forbids anything that would block or redirect the water.",
    whatItIs: [
      "A drainage easement exists so water has somewhere to go. Subdivision engineering routes runoff along swales, into inlets, through pipes, and often into a shared detention or retention basin, and each of those elements needs a recorded corridor or tract so the city, a district, or the HOA can maintain it.",
      "Unlike a utility easement, the burden here is largely about grade. The value of a swale is entirely in its shape: fill it in, build up beside it, or landscape a berm across it and the design capacity is gone. That is why drainage easements typically prohibit any change to the existing grade without written approval.",
      "Drainage corridors interact with floodplain regulation but are not the same thing. A lot can carry a drainage easement while sitting outside any FEMA flood zone, and a lot inside a flood zone may have no easement at all. Both matter for building, and each is checked in a different place.",
    ],
    howItAppears: [
      "A plat note or hatched area labeled \"Drainage Easement\", \"D.E.\", or \"Drainage and Utility Easement\".",
      "A Schedule B exception referencing a drainage easement or a stormwater maintenance agreement.",
      "A recorded stormwater facility maintenance agreement binding the owner to keep a basin or swale functional.",
      "Visible features: concrete flumes, inlets, headwalls, riprap, mowed swales and detention ponds.",
    ],
    buildImpact: [
      "Grade changes are the core restriction, filling, cutting, berming or paving inside the corridor usually needs written approval.",
      "Sheds, patios, retaining walls and pools inside the easement are typically prohibited and frequently ordered removed.",
      "Fences may be allowed only where they do not trap debris; many jurisdictions require a gap at the bottom of a fence crossing a swale.",
      "If a basin sits on your lot, expect a maintenance duty: mowing, keeping inlets clear, and sometimes periodic sediment removal.",
    ],
    buyImpact: [
      "Check the plat for hatching along rear or side lot lines before you plan an addition, pool or shop building.",
      "Ask who maintains the facility. A private stormwater agreement can transfer real recurring cost to you.",
      "Look for standing water lines, silt fans and erosion during a wet-weather visit; the corridor's real behavior differs from the design drawing.",
      "Flood insurance rating depends on the FEMA map, not on the easement, so check both rather than assuming one implies the other.",
    ],
    sampleClause: {
      title: "Plat drainage easement note",
      text:
        "A drainage easement is hereby dedicated over the areas shown hatched hereon for the conveyance and detention of stormwater. No structure, fill, fence, landscaping or other obstruction that would impede the flow of surface water shall be placed within said easement, and the City shall have the right, but not the obligation, to enter and maintain said facilities.",
      annotations: [
        {
          phrase: "conveyance and detention",
          meaning:
            "Two different functions. Conveyance means water moves through; detention means water is held and released slowly.",
        },
        {
          phrase: "no structure, fill, fence, landscaping or other obstruction",
          meaning:
            "Notice how broad this is. Ordinary landscaping choices can violate a drainage easement.",
        },
        {
          phrase: "impede the flow of surface water",
          meaning:
            "The test is functional. A change that alters flow can be a violation even if nothing was technically built.",
        },
        {
          phrase: "the right, but not the obligation, to enter and maintain",
          meaning:
            "The public body may maintain, but it has not promised to. Day-to-day upkeep usually falls on the owner.",
        },
      ],
    },
    howItEnds: [
      "Vacation of the easement by the city or district, generally only after the drainage function is relocated and re-engineered.",
      "Replatting during redevelopment, where a new drainage plan is approved and the old easement released.",
      "A recorded release by the holder once the facility is decommissioned.",
      "Rarely by abandonment, active drainage infrastructure is seldom treated as abandoned while water still uses it.",
    ],
    keyFacts: [
      { label: "Purpose", value: "Move, hold or release stormwater" },
      { label: "Held by", value: "City, county, drainage district or HOA" },
      { label: "Core restriction", value: "No grade changes or obstructions" },
      { label: "Often includes", value: "An owner maintenance duty" },
    ],
    faq: [
      {
        question: "Can I fill in a drainage swale on my own lot?",
        answer:
          "No, not without written approval from the holder. Filling a swale shifts water onto neighboring lots, which is both an easement violation and, in many states, a separate surface-water liability. Correction orders in these cases usually require restoring the original grade at your expense.",
      },
      {
        question: "Who is responsible when a detention pond fails?",
        answer:
          "Whoever the recorded maintenance agreement names. Typically the HOA or the lot owner, with the municipality holding a backstop right to enter and repair and then bill for the work. Read the agreement rather than assuming the city owns the pond.",
      },
      {
        question: "Does a drainage easement mean my lot floods?",
        answer:
          "Not necessarily. It means the lot is part of the neighborhood's drainage design. Whether flooding actually reaches a structure depends on grade, finished floor elevation and the FEMA map, so check the flood zone separately.",
      },
      {
        question: "Can I plant a garden or trees in a drainage easement?",
        answer:
          "Shallow-rooted turf and low plantings that do not obstruct flow are frequently tolerated; trees, raised beds and shrub masses generally are not, because they trap debris and change the section. Ask the holder in writing and keep the answer.",
      },
    ],
    sources: [
      {
        label: "EPA, stormwater management practices",
        url: "https://www.epa.gov/npdes/stormwater-discharges-municipal-sources",
      },
      {
        label: "FEMA Flood Map Service Center",
        url: "https://msc.fema.gov/portal/home",
        note: "Check the flood zone separately from the recorded drainage easement.",
      },
    ],
    relatedGlossary: ["encumbrance", "setback", "buildable-envelope", "servient-estate"],
    relatedQuestions: [
      "what-is-a-drainage-easement",
      "can-i-build-a-shed-over-an-easement",
      "how-to-find-easements-on-my-property",
    ],
    relatedTypes: ["utility-easement", "right-of-way-easement", "conservation-easement"],
  },

  {
    slug: "right-of-way-easement",
    name: "Right-of-way easement",
    shortName: "Right-of-way",
    aliases: ["ROW", "road right-of-way", "railroad right-of-way"],
    summary:
      "A right-of-way is an easement to pass over land along a defined route, the category that covers public road corridors, railroads, trails and many private lanes.",
    whatItIs: [
      "\"Right-of-way\" describes the purpose rather than the mechanism: it is an easement whose function is passage. Public street rights-of-way, rail corridors, transmission line routes, pipeline crossings and private lanes are all commonly labeled this way, which is why the term causes so much confusion.",
      "Public road rights-of-way are usually much wider than the pavement. A 60-foot dedicated right-of-way with 24 feet of asphalt leaves 18 feet on each side for shoulders, sidewalks, utility placement and future widening. Owners are regularly surprised to learn that their mailbox, hedge or fence sits inside the public corridor.",
      "Rail corridors are their own puzzle. Some railroads hold fee title, others hold only an easement, and the difference decides who owns the strip when service ends. Including whether a rails-to-trails conversion is a continuation of the easement or a new use altogether.",
    ],
    howItAppears: [
      "A plat dedication: \"the right-of-way shown hereon is hereby dedicated to public use\".",
      "A deed granting a right-of-way with a stated width and centerline description.",
      "A Schedule B exception for a road, rail or trail corridor.",
      "Municipal GIS layers that show right-of-way lines distinctly from parcel boundaries and pavement edges.",
    ],
    buildImpact: [
      "Setbacks are usually measured from the right-of-way line, not the pavement edge, which pushes buildings farther back than owners expect.",
      "Fences, walls, irrigation, signs and landscaping placed inside a public corridor are typically permitted only by revocable encroachment permit.",
      "Driveway connections need an access or curb-cut permit, and agencies routinely limit the number and spacing of connections.",
      "Sight-distance triangles at corners restrict height inside the corridor, sometimes to as little as two or three feet.",
    ],
    buyImpact: [
      "Compare the survey's right-of-way line with the physical road. The gap is real land you cannot use freely.",
      "Ask whether a widening project is programmed. State and county transportation improvement plans are published and searchable.",
      "For rural parcels, confirm whether the frontage road is a public right-of-way or a private lane governed by an easement, maintenance responsibility follows that answer.",
      "Where a former rail corridor abuts, find out whether the railroad held fee or easement, because reversion rights can affect the boundary.",
    ],
    sampleClause: {
      title: "Right-of-way dedication and grant",
      text:
        "There is hereby dedicated to the public a right-of-way sixty (60) feet in width as shown hereon for street, sidewalk, drainage and utility purposes, together with the right of the governing body to construct, maintain and repair improvements therein, and no permanent improvement shall be placed within said right-of-way except by permit.",
      annotations: [
        {
          phrase: "dedicated to the public",
          meaning:
            "A public dedication rather than a private grant. The holder is the governing body on behalf of everyone.",
        },
        {
          phrase: "sixty (60) feet in width",
          meaning:
            "The corridor width, typically far wider than the paved surface. Setbacks measure from this line.",
        },
        {
          phrase: "street, sidewalk, drainage and utility purposes",
          meaning:
            "A multi-purpose corridor: utilities can be installed within it without a separate easement from you.",
        },
        {
          phrase: "except by permit",
          meaning:
            "Encroachment permits are usually revocable, so an approved fence today can be required to move tomorrow.",
        },
      ],
    },
    howItEnds: [
      "Vacation by the governing body, after which the corridor typically reverts to the abutting owners, often to the centerline.",
      "Relocation as part of a road realignment, with the old corridor released in the same ordinance.",
      "Railroad abandonment proceedings, which in the United States run through the Surface Transportation Board.",
      "Release of a private right-of-way by the benefited owner, recorded like any other easement release.",
    ],
    keyFacts: [
      { label: "Function", value: "Passage along a defined route" },
      { label: "Can be", value: "Public dedication or private grant" },
      { label: "Typical street width", value: "50 to 80 feet, wider than the pavement" },
      { label: "Owner limit", value: "Encroachments only by revocable permit" },
    ],
    faq: [
      {
        question: "Do I own the land between the sidewalk and the street?",
        answer:
          "Often you own to the centerline subject to the public right-of-way, and sometimes the agency holds fee title outright. Either way, use of that strip is controlled by the agency, and you are commonly responsible for mowing it while having little say over what happens there.",
      },
      {
        question: "Can the city remove my fence from the right-of-way?",
        answer:
          "Yes, generally with notice, and often at your expense. Encroachment permits usually state that the improvement is removable at the agency's discretion when the corridor is needed.",
      },
      {
        question: "What is the difference between a right-of-way and an easement?",
        answer:
          "A right-of-way is a kind of easement, one for passage. All rights-of-way are easements when they are not held in fee; not all easements are rights-of-way, since many exist for utilities, drainage or conservation instead of travel.",
      },
      {
        question: "What happens to a rail corridor when the trains stop?",
        answer:
          "It depends on whether the railroad holds fee or easement, and on federal railbanking rules. Under railbanking a corridor can be preserved for future rail use and used as a trail meanwhile, which is why an abandoned line does not automatically return to the adjoining owners.",
      },
    ],
    sources: [
      {
        label: "Surface Transportation Board. Abandonment and railbanking",
        url: "https://www.stb.gov/",
      },
      {
        label: "FHWA, real estate and right-of-way",
        url: "https://www.fhwa.dot.gov/real_estate/",
        note: "Public-agency corridor acquisition and management practice.",
      },
    ],
    relatedGlossary: ["right-of-way", "setback", "encroachment", "buildable-envelope"],
    relatedQuestions: [
      "easement-vs-right-of-way",
      "how-close-to-property-line-can-i-build-a-fence",
      "what-is-encroachment",
    ],
    relatedTypes: ["access-easement", "utility-easement", "drainage-easement"],
  },
];

export function findEasementType(slug: string | undefined): EasementTypeGuide | undefined {
  if (!slug) return undefined;
  return EASEMENT_TYPES.find((t) => t.slug === slug);
}

export const EASEMENT_TYPES_SORTED = [...EASEMENT_TYPES].sort((a, b) =>
  a.name.localeCompare(b.name),
);
